import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { notificationService } from '../services/notificationService'
import { supabase } from '../lib/supabase'
import {
  ArrowLeftIcon,
  CheckIcon,
  CameraIcon,
  BuildingOfficeIcon,
  SparklesIcon,
  BoltIcon,
  ChartBarIcon,
  UserGroupIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'

const UpgradePage = () => {
  const navigate = useNavigate()
  const { user, refreshUser } = useAuth()
  const { showSuccess, showError, showConfirm } = useNotification()
  const [upgrading, setUpgrading] = useState<string | null>(null)

  const plans = [
    {
      id: 'freemium',
      name: 'Freemium',
      price: 'Free',
      period: 'forever',
      description: 'Perfect for getting started with basic shoot management',
      icon: SparklesIcon,
      color: 'from-gray-500 to-gray-600',
      popular: false,
      features: [
        'Up to 5 client projects',
        'Basic shoot scheduling',
        'Simple task management',
        'Email support',
        'Basic invoicing (5/month)',
        'Client portal access'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$29',
      period: '/month',
      description: 'Perfect for individual photographers and videographers',
      icon: CameraIcon,
      color: 'from-blue-500 to-purple-600',
      popular: true,
      features: [
        'Unlimited client projects',
        'Advanced shoot management',
        'AI-powered task automation',
        'Priority email support',
        'Unlimited invoicing & payments',
        'Advanced client portals',
        'File storage (100GB)',
        'Custom branding',
        'Analytics & reports',
        'Mobile app access',
        'Advanced calendar features',
        'Automated workflows'
      ]
    },
    {
      id: 'studio',
      name: 'Studio',
      price: '$99',
      period: '/month',
      description: 'Built for creative studios and growing teams',
      icon: BuildingOfficeIcon,
      color: 'from-purple-500 to-pink-600',
      popular: false,
      features: [
        'Everything in Professional',
        'Team collaboration tools',
        'Multi-user access (up to 10)',
        'Advanced project templates',
        'White-label client portals',
        'File storage (1TB)',
        'Advanced analytics dashboard',
        'API access & integrations',
        'Dedicated account manager',
        'Phone support',
        'Custom integrations',
        'Advanced security features'
      ]
    }
  ]

  const currentPlanFeatures = [
    'Up to 5 client projects',
    'Basic shoot scheduling',
    'Simple task management',
    'Email support',
    'Basic invoicing (5/month)',
    'Client portal access'
  ]



  const handleDowngrade = async () => {
    if (!user) return
    
    showConfirm(
      'Confirm Downgrade',
      'Are you sure you want to downgrade to the Freemium plan? You will lose access to premium features.',
      () => performDowngrade(),
      undefined,
      'Yes, Downgrade',
      'Cancel'
    )
  }

  const performDowngrade = async () => {
    if (!user) return
    
    setUpgrading('freemium')
    
    try {
      if (!supabase) throw new Error('Database not available')
      
      // Update user's subscription status in the database
      const { error } = await supabase
        .from('profiles')
        .update({ 
          subscription_status: 'freemium',
          subscription_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)

      if (error) throw error

      // Create downgrade notification
      const result = await notificationService.createPlanDowngradeNotification(user.id, 'Freemium')
      
      if (!result.success) {
        console.error('Failed to create downgrade notification:', result.error)
      }

      // Refresh user data to reflect the downgrade
      await refreshUser()

      // Show success message
      showSuccess(
        'Plan Changed',
        `Plan changed to Freemium. ${result.success ? 'Check your notifications for details.' : ''}`
      )
      
      // Navigate back to dashboard
      setTimeout(() => navigate('/dashboard'), 2000)
      
    } catch (error) {
      console.error('Error downgrading plan:', error)
      showError('Downgrade Failed', 'Failed to change plan. Please try again.')
    } finally {
      setUpgrading(null)
    }
  }

  // Get current plan info
  const getCurrentPlan = () => {
    const currentStatus = user?.subscriptionStatus || 'freemium'
    if (currentStatus === 'freemium') return { name: 'Freemium', color: 'from-gray-500 to-gray-600' }
    if (currentStatus === 'professional') return { name: 'Professional', color: 'from-blue-500 to-purple-600' }
    if (currentStatus === 'studio') return { name: 'Studio', color: 'from-purple-500 to-pink-600' }
    return { name: 'Freemium', color: 'from-gray-500 to-gray-600' }
  }

  const currentPlan = getCurrentPlan()

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-3 text-white hover:text-gray-300 transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
              <span className="font-medium">Back to Dashboard</span>
            </motion.button>
            
            <motion.button 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/')}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                <CameraIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">
                Shoot<span className="text-gray-400">Suite</span>
              </span>
            </motion.button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-6">
            <SparklesIcon className="w-8 h-8 text-yellow-400 mr-3" />
            <span className="glass-badge px-4 py-2 rounded-full text-white font-medium">
              Unlock Premium Features
            </span>
          </div>
          
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
            Take Your Business to the <span className="text-gradient">Next Level</span>
          </h1>
          
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            You're currently on the <strong className="text-white">{currentPlan.name}</strong> plan. 
            {user?.subscriptionStatus === 'freemium' || !user?.subscriptionStatus 
              ? 'Upgrade to unlock powerful features that will save you time and help you grow your creative business.'
              : 'Manage your subscription and explore additional features below.'
            }
          </p>

          {/* Current Plan Status */}
          {(user?.subscriptionStatus === 'freemium' || !user?.subscriptionStatus) ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-mockup rounded-2xl p-6 max-w-2xl mx-auto mb-12"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Your Current Plan Includes:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {currentPlanFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-yellow-400 text-sm font-medium">
                  ⚠️ You're limited to 5 clients and 5 invoices per month
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-mockup rounded-2xl p-6 max-w-2xl mx-auto mb-12"
            >
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-blue-600 flex items-center justify-center">
                  <CheckIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  🎉 You're on the {user.subscriptionStatus.charAt(0).toUpperCase() + user.subscriptionStatus.slice(1)} Plan!
                </h3>
                <p className="text-gray-400">
                  You have access to all premium features. Enjoy unlimited tasks, AI automation, and advanced analytics.
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Pricing Plans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-12"
        >
          {plans.map((plan, index) => {
            const isCurrentPlan = user?.subscriptionStatus === plan.id
            const isUpgrading = upgrading === plan.id
            
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className={`relative glass-mockup rounded-2xl p-8 transition-all ${
                  isCurrentPlan 
                    ? 'ring-2 ring-emerald-500/50 hover:scale-100' 
                    : 'hover:scale-105'
                } ${plan.popular && !isCurrentPlan ? 'ring-2 ring-blue-500/50' : ''}`}
                whileHover={{ y: isCurrentPlan ? 0 : -5 }}
              >
                {isCurrentPlan && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="glass-badge text-white px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-emerald-500 to-emerald-600">
                      Current Plan
                    </span>
                  </div>
                )}
                
                {plan.popular && !isCurrentPlan && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="glass-badge text-white px-4 py-2 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${plan.color} flex items-center justify-center ${
                    isCurrentPlan ? 'ring-2 ring-emerald-400/50' : ''
                  }`}>
                    <plan.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-400 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-white">{plan.price}</span>
                    <span className="text-gray-400 ml-2">{plan.period}</span>
                  </div>

                  {isCurrentPlan ? (
                    <div className="space-y-3">
                      <div className="w-full py-3 px-6 rounded-xl font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        ✓ Active Plan
                      </div>
                      {plan.id !== 'freemium' && (
                        <motion.button
                          onClick={handleDowngrade}
                          disabled={isUpgrading}
                          className="w-full py-2 px-4 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-all disabled:opacity-50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {upgrading === 'freemium' ? 'Downgrading...' : 'Downgrade to Freemium'}
                        </motion.button>
                      )}
                    </div>
                  ) : plan.id === 'freemium' && (user?.subscriptionStatus === 'freemium' || !user?.subscriptionStatus) ? (
                    // Hide button for freemium plan if user is already on freemium
                    <div className="w-full py-3 px-6 rounded-xl font-semibold bg-gray-500/20 text-gray-400 border border-gray-500/30">
                      Current Plan
                    </div>
                  ) : (
                    <motion.button
                      onClick={() => {
                        if (plan.id === 'freemium') {
                          handleDowngrade()
                        } else {
                          // Redirect to checkout page for paid plans
                          navigate(`/checkout?plan=${plan.id}`)
                        }
                      }}
                      disabled={isUpgrading}
                      className={`w-full py-3 px-6 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                        plan.id === 'freemium'
                          ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20'
                          : plan.popular
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700'
                          : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                      }`}
                      whileHover={{ scale: isUpgrading ? 1 : 1.02 }}
                      whileTap={{ scale: isUpgrading ? 1 : 0.98 }}
                    >
                      {isUpgrading ? 
                        (plan.id === 'freemium' ? 'Downgrading...' : 'Processing...') : 
                        (plan.id === 'freemium' ? 'Downgrade to Freemium' : `Subscribe to ${plan.name}`)
                      }
                    </motion.button>
                  )}
                                </div>

                <div className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3">
                      <CheckIcon className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-white mb-8">Why Upgrade?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: BoltIcon,
                title: 'Save Time',
                description: 'Automate repetitive tasks and focus on creativity'
              },
              {
                icon: ChartBarIcon,
                title: 'Grow Revenue',
                description: 'Professional invoicing and payment processing'
              },
              {
                icon: UserGroupIcon,
                title: 'Scale Business',
                description: 'Manage unlimited clients and projects'
              },
              {
                icon: ShieldCheckIcon,
                title: 'Stay Secure',
                description: 'Advanced security and data protection'
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="glass-mockup rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="glass-mockup rounded-2xl p-8 max-w-4xl mx-auto"
        >
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            {[
              {
                question: "Can I cancel anytime?",
                answer: "Yes! You can cancel your subscription at any time. You'll continue to have access to premium features until the end of your billing period."
              },
              {
                question: "What happens to my data if I downgrade?",
                answer: "Your data is always safe. If you downgrade, you'll keep all your existing clients and shoots, but you'll be limited to the freemium plan restrictions for new content."
              },
              {
                question: "Do you offer refunds?",
                answer: "We offer a 30-day money-back guarantee. If you're not satisfied with your upgrade, contact us within 30 days for a full refund."
              },
              {
                question: "Can I upgrade or downgrade my plan later?",
                answer: "Absolutely! You can change your plan at any time from your account settings. Changes take effect immediately."
              }
            ].map((faq, index) => (
              <div key={index} className="border-b border-white/10 pb-4 last:border-b-0">
                <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                <p className="text-gray-400">{faq.answer}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </main>
    </div>
  )
}

export default UpgradePage 